import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { retryBackoff } from "backoff-rxjs";
import { combineLatest, concatMap, filter, Subject, tap } from "rxjs";
import {
	Action,
	ActionType,
	CreateAssetDispatchAction,
	CreateDispatchAction,
	CreateNoteAction,
	UpdateQueueTimeAction,
} from "./action.model";
import { v4 as uuidv4 } from "uuid";
import { ActionsStore } from "./actions.store";
import { addEntities, deleteEntities, selectFirst } from "@ngneat/elf-entities";
import { EnvironmentQuery } from "./environment.query";
import { UiQuery } from "./ui.query";
import { LocationStateQuery } from "./location-state.query";
import { LocationStateService } from "./location-state.service";
import { LastRidersUpdate } from "./location-state.store";
import { ImageAttachment } from "source/app/features/checklists/checklist.interface";
import { AssetDispatchService } from "../services/asset-dispatch.service";
import { DispatchUndoService } from "../services/dispatch-undo.service";
import { RideOpsUiService } from "../services/rideops-ui.service";

@Injectable({ providedIn: "root" })
export class ActionsService {
	constructor(
		private actionsStore: ActionsStore,
		private locationStateQuery: LocationStateQuery,
		private locationStateService: LocationStateService,
		private environmentQuery: EnvironmentQuery,
		private uiQuery: UiQuery,
		private http: HttpClient,
		private assetDispatchService: AssetDispatchService,
		private dispatchUndoService: DispatchUndoService,
		private rideOpsUi: RideOpsUiService,
	) {
		this.sendActions();
	}

	INIT_INTERVAL_MS = 100; // 100 ms
	MAX_INTERVAL_MS = 60 * 1000; // 60 sec

	actionSentToServer$ = new Subject<Action<unknown>>();

	addCreateNoteAction = async (description: string, type: string, attachments: ImageAttachment[], title?: string) => {
		const operator = await this.locationStateQuery.operator$.firstAsync();
		const location = await this.locationStateQuery.location$.firstAsync();
		const now = new Date();

		const action: Action<CreateNoteAction> = {
			id: uuidv4(),
			actionType: ActionType.CREATE_NOTE,
			location: location.id,
			when: now.toISOString(),
			operator: operator.id,
			payLoad: {
				type: type,
				description: description,
				attachments: attachments,
				...(title != null && title !== "" ? { title } : {}),
			},
		};

		this.actionsStore.store.update(addEntities(action));
	};

	addCreateDispatchAction = async (riders: number, dispatchDelayReason?: number) => {
		const [operator, location, rideOpsSettings, carts, seats] = await combineLatest([
			this.locationStateQuery.operator$,
			this.locationStateQuery.location$,
			this.locationStateQuery.rideOpsSettings$,
			this.uiQuery.cartsInUse$,
			this.uiQuery.seatsUnavailable$,
		]).firstAsync();
		const now = new Date();
		const previousLastRidersUpdate = this.locationStateQuery.getSnapshot().lastRidersUpdate;

		const action: Action<CreateDispatchAction> = {
			id: uuidv4(),
			actionType: ActionType.CREATE_DISPATCH,
			location: location.id,
			when: now.toISOString(),
			operator: operator.id,
			payLoad: {
				riders: riders,
				dispatchUnitCapacity: rideOpsSettings.dispatchUnitCapacity,
				dispatchUnitsAvailable: carts,
				totalUnavailableCapacity: seats,
				dispatchDelayReason: dispatchDelayReason,
			},
		};

		this.locationStateService.updateDispatchesAndRiders(now, riders);
		this.enqueueOrHoldDispatch(action, { riders, previousLastRidersUpdate });
	};

	// Dispatch the "in action" unit of the asset queue via `create-asset-dispatch` (one Raven
	// DispatchEntry + asset_dispatches row per tap), then rotate the queue. The queue index is
	// advanced here (optimistically, once per dispatch) instead of on confirmation, so a real
	// dispatch never double-advances and a rapid second dispatch already targets the next unit.
	// No current unit (none configured, or all marked unavailable) → no-op; never fall back to a
	// location create-dispatch while in asset mode.
	addCreateAssetDispatchAction = async (riders: number, delayReason?: number, emptyReason?: number) => {
		const [operator, location, currentUnit] = await combineLatest([
			this.locationStateQuery.operator$,
			this.locationStateQuery.location$,
			this.assetDispatchService.currentUnit$,
		]).firstAsync();

		if (currentUnit == null) {
			return;
		}

		const snapshot = this.locationStateQuery.getSnapshot();
		const previousUnitCount = snapshot.lastAssetDispatchCountByUnitId?.[currentUnit.id];
		const previousLastOccupiedSeats = snapshot.lastAssetDispatchOccupiedSeats;
		const previousLastRidersUpdate = snapshot.lastRidersUpdate;

		const now = new Date();
		const unavailableSeats = this.uiQuery.unavailableSeatCountForUnit(currentUnit.id);

		const action: Action<CreateAssetDispatchAction> = {
			id: uuidv4(),
			actionType: ActionType.CREATE_ASSET_DISPATCH,
			location: location.id,
			when: now.toISOString(),
			operator: operator.id,
			payLoad: {
				asset: currentUnit.id,
				seats: currentUnit.capacity,
				occupiedSeats: riders,
				unavailableSeats,
				delayReason: delayReason,
				emptyReason: emptyReason,
			},
		};

		this.locationStateService.updateDispatchesAndRiders(now, riders);
		this.locationStateService.advanceAssetDispatchQueueIndex(riders);
		this.locationStateService.setLastAssetDispatchCountForUnit(currentUnit.id, riders);
		this.enqueueOrHoldDispatch(action, {
			riders,
			unitId: currentUnit.id,
			unitName: currentUnit.name,
			previousUnitCount,
			previousLastOccupiedSeats,
			previousLastRidersUpdate,
		});
	};

	private enqueueOrHoldDispatch = (
		action: Action<CreateDispatchAction | CreateAssetDispatchAction>,
		options: {
			riders: number;
			unitId?: string;
			unitName?: string;
			previousUnitCount?: number;
			previousLastOccupiedSeats?: number;
			previousLastRidersUpdate?: LastRidersUpdate;
		},
	) => {
		if (!this.rideOpsUi.isV2Active() || !this.uiQuery.getDispatchUndoOptIn()) {
			this.actionsStore.store.update(addEntities(action));
			return;
		}

		void this.dispatchUndoService.arm(action, options);
	};

	addSignOutAction = async () => {
		const operator = await this.locationStateQuery.operator$.firstAsync();
		const location = await this.locationStateQuery.location$.firstAsync();
		const now = new Date();

		const action: Action<object> = {
			id: uuidv4(),
			actionType: ActionType.SIGN_OUT,
			location: location.id,
			when: now.toISOString(),
			operator: operator.id,
			payLoad: {},
		};

		this.actionsStore.store.update(addEntities(action));
	};

	addUpdateQueueTimeAction = async (queueTimeInMinutes: number) => {
		const operator = await this.locationStateQuery.operator$.firstAsync();
		const location = await this.locationStateQuery.location$.firstAsync();
		const now = new Date();

		const action: Action<UpdateQueueTimeAction> = {
			id: uuidv4(),
			actionType: ActionType.UPDATE_QUEUE_TIME,
			location: location.id,
			when: now.toISOString(),
			operator: operator.id,
			payLoad: {
				queueTimeInMinutes: queueTimeInMinutes,
			},
		};

		this.actionsStore.store.update(addEntities(action));
		this.locationStateService.updateQueueTime(now, queueTimeInMinutes);
	};

	private sendActions = () => {
		combineLatest([
			this.environmentQuery.environment$,
			this.actionsStore.store.pipe(selectFirst()),
			this.locationStateQuery.selectOperator$,
		])
			.pipe(
				// we dont want to do anything, if the actions are empty or if no operator is loggedin, as the endpoints require auth
				filter(([_environment, action, operator]) => action !== undefined && operator !== undefined),
				concatMap(([environment, action]) => {
					const endpoint = environment.api.url + this.getEndpointUrl(action);
					const request = this.getRequest(action);

					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					return this.http.post<ApiResponseWithData<any>>(endpoint, request).pipe(
						tap((response) => {
							if (response.success == false) {
								return;
							}

							this.actionsStore.store.update(deleteEntities(action.id));
							this.actionSentToServer$.next(action);
						}),
					);
				}),
				retryBackoff({
					initialInterval: this.INIT_INTERVAL_MS,
					maxInterval: this.MAX_INTERVAL_MS,
					resetOnSuccess: true,
				}),
			)
			.subscribe();
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private getEndpointUrl = (action: Action<any>): string => {
		switch (action.actionType) {
			case ActionType.CREATE_DISPATCH:
				return "api/rideops/create-dispatch";
			case ActionType.CREATE_ASSET_DISPATCH:
				return "api/rideops/create-asset-dispatch";
			case ActionType.UPDATE_QUEUE_TIME:
				return "api/rideops/update-queue";
			case ActionType.CREATE_NOTE:
				return "api/rideops/create-note";
			case ActionType.SIGN_OUT:
				return "api/rideops/logout";
			default:
				throw new Error("ActionType '" + action.actionType + "' was unknown.");
		}
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private getRequest = (action: Action<any>): BackendRequest => {
		switch (action.actionType) {
			case ActionType.CREATE_DISPATCH:
				return this.getCreateDispatchRequest(action);
			case ActionType.CREATE_ASSET_DISPATCH:
				return this.getCreateAssetDispatchRequest(action);
			case ActionType.UPDATE_QUEUE_TIME:
				return this.getUpdateQueueTimeRequest(action);
			case ActionType.CREATE_NOTE:
				return this.getCreateNoteRequest(action);
			case ActionType.SIGN_OUT:
				return this.getSignoutRequest(action);
			default:
				throw new Error("ActionType '" + action.actionType + "' was unknown.");
		}
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private getSignoutRequest = (action: Action<any>): any => {
		return {
			when: action.when,
			location: action.location,
			user: action.operator,
		};
	};

	private getCreateDispatchRequest = (action: Action<CreateDispatchAction>): CreateDispatchRequest => {
		const request = {
			when: action.when,
			operator: action.operator,
			location: action.location,
			riders: action.payLoad.riders,
			dispatchUnitCapacity: action.payLoad.dispatchUnitCapacity,
			dispatchUnitsAvailable: action.payLoad.dispatchUnitsAvailable,
			totalUnavailableCapacity: action.payLoad.totalUnavailableCapacity,
			dispatchDelayReason: action.payLoad.dispatchDelayReason ?? 0,
		};

		if (request.dispatchUnitCapacity <= 0) {
			request.dispatchUnitCapacity = 1;
		}

		if (request.dispatchUnitsAvailable <= 0) {
			request.dispatchUnitsAvailable = 1;
		}

		if (request.riders < 0) {
			request.riders = 0;
		}

		return request;
	};

	// Maps to the backend `create-asset-dispatch` contract:
	// { location, asset, operator, when, seats, occupiedSeats, unavailableSeats, delayReason: null, emptyReason: null }.
	// `delayReason` and `emptyReason` are nullable: send `null` (not 0) when no reason is set. The backend
	// response { runId, runCompleted, aggregatedLocationDispatchId } is not consumed here.
	private getCreateAssetDispatchRequest = (action: Action<CreateAssetDispatchAction>): CreateAssetDispatchRequest => {
		const seats = action.payLoad.seats > 0 ? action.payLoad.seats : 1;
		const occupiedSeats = action.payLoad.occupiedSeats < 0 ? 0 : action.payLoad.occupiedSeats;
		const unavailableSeats = Math.min(Math.max(action.payLoad.unavailableSeats, 0), seats);
		const legacyPayload = action.payLoad as CreateAssetDispatchAction & {
			dispatchDelayReason?: number;
			dispatchEmptyReason?: number;
		};
		const rawDelayReason = action.payLoad.delayReason ?? legacyPayload.dispatchDelayReason;
		const rawEmptyReason = action.payLoad.emptyReason ?? legacyPayload.dispatchEmptyReason;
		const delayReason = rawDelayReason != null && rawDelayReason > 0 ? rawDelayReason : null;
		const emptyReason = rawEmptyReason != null && rawEmptyReason > 0 ? rawEmptyReason : null;

		return {
			when: action.when,
			operator: action.operator,
			location: action.location,
			asset: action.payLoad.asset,
			seats: seats,
			occupiedSeats: occupiedSeats,
			unavailableSeats: unavailableSeats,
			delayReason: delayReason,
			emptyReason: emptyReason,
		};
	};

	private getUpdateQueueTimeRequest = (action: Action<UpdateQueueTimeAction>): UpdateQueueTimeRequest => {
		return {
			when: action.when,
			operator: action.operator,
			location: action.location,
			queueTimeInMinutes: action.payLoad.queueTimeInMinutes,
		};
	};

	private getCreateNoteRequest = (action: Action<CreateNoteAction>): CreateNoteRequest => {
		return {
			when: action.when,
			operator: action.operator,
			location: action.location,
			description: action.payLoad.description,
			type: action.payLoad.type,
			attachments: action.payLoad.attachments,
			...(action.payLoad.title != null && action.payLoad.title !== "" ? { title: action.payLoad.title } : {}),
		};
	};
}

export interface BackendRequest {
	operator: string;
	location?: string;
	when?: string;
}

export interface CreateNoteRequest extends BackendRequest {
	description: string;
	type: string;
	attachments: ImageAttachment[];
	title?: string;
}

export interface UpdateQueueTimeRequest extends BackendRequest {
	queueTimeInMinutes: number;
}

export interface CreateDispatchRequest extends BackendRequest {
	riders: number;
	dispatchUnitCapacity: number;
	dispatchUnitsAvailable: number;
	totalUnavailableCapacity: number;
	dispatchDelayReason?: number;
}

export interface CreateAssetDispatchRequest extends BackendRequest {
	asset: string;
	seats: number;
	occupiedSeats: number;
	unavailableSeats: number;
	delayReason: number | null;
	emptyReason: number | null;
}
